// @ts-expect-error TODO(typecheck-unify): TS6133 'React' is declared but its value is never read.
import React, { useEffect, useMemo, useState, Fragment } from "react";
import { Modal, Table, Button, Alert, Space, Tooltip, Input } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import axios from "axios";
import request from "superagent";

import { getAuthHeader } from "../../utils/superagent";
import { showErrorToast, showSuccessToast } from "@src/appV2/lib/Notifications";
import { ROLE_MAPPINGS } from "../teamMembers/constants";
import { getHighestAccessRole } from "../teamMembers/utils";
import { environmentConfig } from "@src/appV2/environment";
import { CbhFeatureFlag, useCbhFlag } from "@src/appV2/FeatureFlags";
import { useImpersonationSessions } from "@src/appV2/ImpersonationSessions/ImpersonationSessionsProvider";
import { useEffectivePermissions } from "@src/appV2/Auth/hooks/useEffectivePermissions";
import { useMintAdminMagicLink } from "./useMintAdminMagicLink";

function showMintError(err: unknown): void {
  if (axios.isAxiosError(err)) {
    const status = err.response?.status;
    const responseData = err.response?.data as
      | { message?: string; retryAfterSeconds?: number }
      | undefined;
    const message = responseData?.message;

    if (status === 404) {
      showErrorToast(
        "No Cognito account for this email yet. Ask them to attempt login once — that'll provision the account, then this link will work."
      );
      return;
    }
    if (status === 422) {
      showErrorToast(message ?? "This user can't be issued a login link right now.");
      return;
    }
    if (status === 429) {
      const retryAfter = responseData?.retryAfterSeconds;
      showErrorToast(
        retryAfter !== undefined
          ? `Too many requests — try again in ${retryAfter} seconds.`
          : (message ?? "Too many requests. Try again shortly.")
      );
      return;
    }
    if (message) {
      showErrorToast(message);
      return;
    }
  }
  if (err instanceof Error) {
    showErrorToast(err.message);
    return;
  }
  showErrorToast("Failed to mint login link.");
}

interface ActiveUser {
  _id: string;
  name: string;
  email: string;
  roles?: string[];
}

// @ts-expect-error TODO(typecheck-unify): TS7031 Binding element 'visible' implicitly has an 'any' type.
function AutoLoginLinksModal({ visible, onCancel, facilityId }) {
  const [users, setUsers] = useState<ActiveUser[]>([]);
  const [isLoading, setLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState<string | null>(null);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [link, setLink] = useState<string | null>(null);
  const [nameFilter, setNameFilter] = useState("");

  const isWorkplaceCognitoImpersonationEnabled = useCbhFlag(
    CbhFeatureFlag.WORKPLACE_COGNITO_IMPERSONATION,
    { defaultValue: false }
  );
  const { openSession } = useImpersonationSessions();
  const { canImpersonateUsers } = useEffectivePermissions();
  const mintMagicLink = useMintAdminMagicLink();

  useEffect(() => {
    if (!visible) {
      return;
    }
    setNameFilter("");
    const getActiveUsers = async () => {
      void request
        // @ts-expect-error TODO(typecheck-unify): TS7017 Element implicitly has an 'any' type because type 'typeof globalThis' has no index signature.
        .get(`${global.api}/facilityUser/activeUsers`)
        .query({ facilityId })
        .set(await getAuthHeader())
        .then(({ body }) => {
          setUsers(body.users);
          setLoading(false);
        });
    };
    setLoading(true);
    void getActiveUsers();
  }, [facilityId, visible]);

  const filteredUsers = useMemo(() => {
    const sorted = [...users].sort((a, b) =>
      (a.name ?? "").localeCompare(b.name ?? "", undefined, { sensitivity: "base" })
    );
    const term = nameFilter.trim().toLowerCase();
    if (!term) {
      return sorted;
    }
    return sorted.filter((user) => user.name?.toLowerCase().includes(term));
  }, [users, nameFilter]);

  const fetchLink = (email: string) => async () => {
    setSelectedUser(email);

    try {
      const { loginLink } = await mintMagicLink.mutateAsync({
        email,
        requesterUrl: `${environmentConfig.REACT_APP_ADMIN_APP_CLIENT_URL}/v2/email-login-link`,
      });

      if (navigator.clipboard) {
        await navigator.clipboard.writeText(loginLink);
        showSuccessToast("Login link copied to clipboard");
      } else {
        setLink(loginLink);
        setShowLinkModal(true);
      }
    } catch (err: unknown) {
      showMintError(err);
    } finally {
      setSelectedUser(null);
    }
  };

  const handleImpersonate = (user: { _id: string; name: string; email: string }) => {
    if (!canImpersonateUsers) {
      return;
    }

    openSession(
      "workplace",
      environmentConfig.REACT_APP_WORKPLACE_APP_URL,
      user._id,
      user.name ?? user.email,
      user.email
    );
    onCancel();
  };

  // @ts-expect-error TODO(typecheck-unify): TS7006 Parameter 'email' implicitly has an 'any' type.
  const renderActionButtons = (email, record) => {
    if (isWorkplaceCognitoImpersonationEnabled) {
      return (
        <Space size="small" style={{ whiteSpace: "nowrap" }}>
          <Tooltip
            title={canImpersonateUsers ? "" : "You don't have permission to impersonate users."}
          >
            <Button
              size="small"
              type="primary"
              disabled={!canImpersonateUsers}
              onClick={() => handleImpersonate(record)}
            >
              Impersonate
            </Button>
          </Tooltip>
          <Button
            size="small"
            onClick={fetchLink(email)}
            loading={email === selectedUser}
            style={{ opacity: 0.6 }}
          >
            Copy link (legacy)
          </Button>
        </Space>
      );
    }

    return (
      <Button type="primary" onClick={fetchLink(email)} loading={email === selectedUser}>
        Click to copy link
      </Button>
    );
  };

  const renderRole = (roles: string[] | undefined) => {
    // @ts-expect-error TODO(typecheck-unify): TS7053 Element implicitly has an 'any' type because expression of type 'any' can't be used to index type '{ CORP: { label: string; position:
    return ROLE_MAPPINGS[getHighestAccessRole(roles, true)]?.label;
  };

  const handleCloseLinkModal = () => {
    setLink(null);
    setShowLinkModal(false);
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      ellipsis: true,
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      ellipsis: true,
    },
    {
      title: "Role",
      dataIndex: "roles",
      render: renderRole,
      key: "roles",
      width: 120,
    },
    {
      title: "Actions",
      dataIndex: "email",
      key: "_id",
      render: renderActionButtons,
      width: isWorkplaceCognitoImpersonationEnabled ? 250 : 160,
    },
  ];

  return (
    <Fragment>
      <Modal
        title="Auto Login Links"
        visible={visible}
        width={800}
        onCancel={onCancel}
        footer={null}
      >
        <Alert
          style={{ marginBottom: "10px" }}
          message={`Please open the login link in incognito browser or log out of your account first.`}
          type="info"
          showIcon
        />
        {isWorkplaceCognitoImpersonationEnabled && !canImpersonateUsers && (
          <Alert
            style={{ marginBottom: "10px" }}
            message="You need the Impersonation role to impersonate workplace users. Please contact an administrator to request access."
            type="error"
            showIcon
          />
        )}
        <Input
          placeholder="Filter by name"
          prefix={<SearchOutlined />}
          value={nameFilter}
          onChange={(event) => setNameFilter(event.target.value)}
          allowClear
          style={{ marginBottom: 10, maxWidth: 400 }}
        />
        <Table
          rowKey="_id"
          dataSource={filteredUsers}
          loading={isLoading}
          columns={columns}
          pagination={false}
        />
      </Modal>
      <Modal
        title="Oops, unable to copy link to clipboard"
        visible={showLinkModal}
        onCancel={handleCloseLinkModal}
        footer={null}
      >
        <p style={{ marginBottom: "10px" }}>Please copy the following link:</p>
        <p>{link}</p>
      </Modal>
    </Fragment>
  );
}

export { AutoLoginLinksModal };
